import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, ShoppingCart, DollarSign, TrendingUp,
  LogOut, ChevronDown, Search, RefreshCw, ExternalLink,
  Download, Package, FileText, Save, X, Pencil, Loader2, ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "/logo.webp";

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600',
  approved: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-yellow-100 text-yellow-700',
  declined: 'bg-red-100 text-red-700',
  error: 'bg-red-100 text-red-700',
};

const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'closed'];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="text-sm text-gray-500 font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

// ===== CSV EXPORT =====
function downloadCSV(data, filename, columns) {
  const header = columns.map(c => c.label).join(',');
  const rows = data.map(row =>
    columns.map(c => {
      let val = c.accessor(row);
      if (val === null || val === undefined) val = '';
      val = String(val).replace(/"/g, '""');
      if (val.includes(',') || val.includes('"') || val.includes('\n')) val = `"${val}"`;
      return val;
    }).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ===== PRODUCT EDIT MODAL =====
function ProductEditModal({ product, onClose, onSave, saving }) {
  const [form, setForm] = useState({ ...product });
  const [featuresText, setFeaturesText] = useState((product.features || []).join('\n'));
  const [installImagesText, setInstallImagesText] = useState((product.install_images || []).join('\n'));

  const handleSave = () => {
    const data = {
      ...form,
      features: featuresText.split('\n').map(f => f.trim()).filter(Boolean),
      install_images: installImagesText.split('\n').map(u => u.trim()).filter(Boolean),
    };
    onSave(product.id, data);
  };

  const field = (label, key, type = 'text') => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={form[key] ?? ''}
        onChange={e => setForm(prev => ({ ...prev, [key]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid={`product-edit-${key}`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="product-edit-modal">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-bold text-lg text-gray-900">Edit Product</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          {field('Product Name', 'name')}
          <div className="grid grid-cols-2 gap-4">
            {field('Model Number', 'model_number')}
            {field('Tonnage', 'tonnage')}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {field('SEER Rating', 'seer_rating')}
            {field('Refrigerant', 'refrigerant')}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {field('MAP Price', 'map_price', 'number')}
            {field('Sale Price', 'sale_price', 'number')}
            {field('Installed Price', 'installed_price', 'number')}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {field('Unit Monthly', 'unit_monthly', 'number')}
            {field('Installed Monthly', 'installed_monthly', 'number')}
          </div>
          {field('Main Image URL', 'image_url')}
          {field('Warranty', 'warranty')}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Install Images (one URL per line)</label>
            <textarea
              value={installImagesText}
              onChange={e => setInstallImagesText(e.target.value)}
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="product-edit-install-images"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Features (one per line)</label>
            <textarea
              value={featuresText}
              onChange={e => setFeaturesText(e.target.value)}
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="product-edit-features"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 rounded-lg font-medium flex items-center gap-2"
            data-testid="product-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== SITE CONTENT EDITOR =====
function SiteContentEditor({ authHeaders }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/site-content`, { headers: authHeaders() });
        if (res.ok) setContent(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/site-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(content),
      });
      if (res.ok) toast.success('Site content saved!');
      else toast.error('Failed to save');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const updateField = (section, key, value) => {
    setContent(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!content) return <p className="text-gray-500 text-center py-12">Unable to load site content</p>;

  const textField = (section, key, label, multiline = false) => (
    <div key={`${section}-${key}`}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={content[section]?.[key] || ''}
          onChange={e => updateField(section, key, e.target.value)}
          rows={6}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid={`cms-${section}-${key}`}
        />
      ) : (
        <input
          value={content[section]?.[key] || ''}
          onChange={e => updateField(section, key, e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid={`cms-${section}-${key}`}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><FileText className="w-4 h-4" /> Hero Section</h3>
        <div className="space-y-3">
          {textField('hero', 'badge_text', 'Badge Text (e.g. "Limited Time Offer...")')}
          {textField('hero', 'headline', 'Headline')}
          {textField('hero', 'subtitle', 'Subtitle')}
          {textField('hero', 'promo_line1', 'Promo Line 1 (Yellow text)')}
          {textField('hero', 'promo_line2', 'Promo Line 2 (Yellow text)')}
        </div>
      </div>

      {/* Terms */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Terms & Conditions</h3>
        <div className="space-y-3">
          {textField('terms', 'title', 'Page Title')}
          {textField('terms', 'content', 'Content (plain text or HTML)', true)}
        </div>
      </div>

      {/* Privacy */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Privacy Policy</h3>
        <div className="space-y-3">
          {textField('privacy', 'title', 'Page Title')}
          {textField('privacy', 'content', 'Content (plain text or HTML)', true)}
        </div>
      </div>

      {/* Accessibility */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Accessibility Statement</h3>
        <div className="space-y-3">
          {textField('accessibility', 'title', 'Page Title')}
          {textField('accessibility', 'content', 'Content (plain text or HTML)', true)}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 text-sm text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 rounded-lg font-semibold flex items-center gap-2"
          data-testid="cms-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
}

// ===== MAIN DASHBOARD =====
export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    const headers = authHeaders();
    try {
      const [statsRes, leadsRes, ordersRes, productsRes, cartsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/leads`, { headers }),
        fetch(`${API_URL}/api/admin/orders`, { headers }),
        fetch(`${API_URL}/api/admin/products`, { headers }),
        fetch(`${API_URL}/api/admin/abandoned-carts`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (leadsRes.ok) setLeads(await leadsRes.json());
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (cartsRes.ok) setAbandonedCarts(await cartsRes.json());
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const handleLeadStatusChange = async (leadId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/leads/${leadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    } catch (err) { console.error('Failed to update lead:', err); }
  };

  const handleProductSave = async (productId, data) => {
    setSavingProduct(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        const updated = await res.json();
        setProducts(prev => prev.map(p => p.id === productId ? updated : p));
        setEditProduct(null);
        toast.success('Product updated!');
      } else {
        toast.error('Failed to update product');
      }
    } catch { toast.error('Save failed'); }
    finally { setSavingProduct(false); }
  };

  const exportLeadsCSV = () => {
    downloadCSV(filteredLeads, 'leads_export.csv', [
      { label: 'Name', accessor: r => r.name },
      { label: 'Email', accessor: r => r.email },
      { label: 'Phone', accessor: r => r.phone },
      { label: 'County', accessor: r => r.county },
      { label: 'Address', accessor: r => r.address },
      { label: 'Product', accessor: r => r.product_name },
      { label: 'Status', accessor: r => r.status },
      { label: 'Notes', accessor: r => r.notes },
      { label: 'Date', accessor: r => r.created_at },
    ]);
    toast.success('Leads exported!');
  };

  const exportOrdersCSV = () => {
    downloadCSV(filteredOrders, 'orders_export.csv', [
      { label: 'Order ID', accessor: r => r.id },
      { label: 'Customer', accessor: r => r.customer_name },
      { label: 'Email', accessor: r => r.customer_email },
      { label: 'Phone', accessor: r => r.customer_phone },
      { label: 'Address', accessor: r => r.customer_address },
      { label: 'County', accessor: r => r.county },
      { label: 'Items', accessor: r => (r.items || []).map(i => i.product_name).join('; ') },
      { label: 'Subtotal', accessor: r => r.subtotal },
      { label: 'Tax', accessor: r => r.total_tax },
      { label: 'Total', accessor: r => r.total },
      { label: 'Status', accessor: r => r.payment_status },
      { label: 'Transaction ID', accessor: r => r.transaction_id },
      { label: 'Date', accessor: r => r.created_at },
    ]);
    toast.success('Orders exported!');
  };

  const handleCartRecovered = async (sessionId, recovered) => {
    try {
      await fetch(`${API_URL}/api/admin/abandoned-carts/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ recovered })
      });
      setAbandonedCarts(prev => prev.map(c => c.session_id === sessionId ? { ...c, recovered } : c));
    } catch (err) { console.error(err); }
  };

  const exportAbandonedCSV = () => {
    downloadCSV(abandonedCarts.filter(c => !c.recovered), 'abandoned_carts_export.csv', [
      { label: 'Date', accessor: r => r.updated_at || r.created_at },
      { label: 'Stage', accessor: r => r.stage },
      { label: 'Name', accessor: r => r.customer_name },
      { label: 'Email', accessor: r => r.customer_email },
      { label: 'Phone', accessor: r => r.customer_phone },
      { label: 'County', accessor: r => r.county },
      { label: 'Items', accessor: r => (r.items || []).map(i => i.name).join('; ') },
      { label: 'Subtotal', accessor: r => r.subtotal },
      { label: 'Recovered', accessor: r => r.recovered ? 'Yes' : 'No' },
    ]);
    toast.success('Abandoned carts exported!');
  };

  const filteredLeads = leads.filter(l =>
    !searchQuery ||
    l.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    l.county?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredOrders = orders.filter(o =>
    !searchQuery ||
    o.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.customer_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Appliance Direct" className="h-8" />
            <span className="text-sm font-semibold text-gray-700 hidden sm:block">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-emerald-600 flex items-center gap-1" data-testid="view-site-link">
              <ExternalLink className="w-3.5 h-3.5" /> View Site
            </a>
            <span className="text-sm text-gray-400">|</span>
            <span className="text-sm text-gray-600" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={handleLogout} className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-gray-100" data-testid="logout-button" title="Logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="stats-grid">
          <StatCard icon={Users} label="Total Leads" value={stats?.total_leads ?? '-'} color="bg-blue-500" />
          <StatCard icon={TrendingUp} label="New Leads" value={stats?.new_leads ?? '-'} color="bg-emerald-500" />
          <StatCard icon={ShoppingCart} label="Orders" value={stats?.total_orders ?? '-'} color="bg-purple-500" />
          <StatCard icon={DollarSign} label="Revenue" value={stats ? formatCurrency(stats.total_revenue) : '-'} color="bg-amber-500" />
          <StatCard icon={ShoppingBag} label="Abandoned Carts" value={stats?.abandoned_carts ?? '-'} color="bg-red-500" />
        </div>

        {/* Tabs + Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto" data-testid="dashboard-tabs">
            {[
              { key: 'leads', label: `Leads (${leads.length})` },
              { key: 'orders', label: `Orders (${orders.length})` },
              { key: 'abandoned', label: `Abandoned (${abandonedCarts.filter(c => !c.recovered).length})` },
              { key: 'products', label: `Products (${products.length})` },
              { key: 'content', label: 'Site Content' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {(activeTab === 'leads' || activeTab === 'orders' || activeTab === 'abandoned') && (
              <>
                {(activeTab === 'leads' || activeTab === 'orders') && (
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      placeholder={`Search ${activeTab}...`}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="search-input"
                    />
                  </div>
                )}
                <button
                  onClick={activeTab === 'leads' ? exportLeadsCSV : activeTab === 'orders' ? exportOrdersCSV : exportAbandonedCSV}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-500 hover:text-emerald-600"
                  data-testid={`export-${activeTab}-csv`}
                  title={`Export ${activeTab} as CSV`}
                >
                  <Download className="w-4 h-4" />
                </button>
              </>
            )}
            <button onClick={fetchData} disabled={refreshing} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50" data-testid="refresh-button" title="Refresh">
              <RefreshCw className={`w-4 h-4 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* LEADS TAB */}
        {activeTab === 'leads' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="data-table-container">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Name</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Contact</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden md:table-cell">County</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden lg:table-cell">Product</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden md:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-gray-400 py-12 text-sm">No leads found</td></tr>
                  ) : filteredLeads.map((lead) => (
                    <tr key={lead.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <td className="py-3 px-4"><span className="font-medium text-gray-900 text-sm">{lead.name}</span></td>
                      <td className="py-3 px-4">
                        <div className="text-sm text-gray-600">{lead.email}</div>
                        <div className="text-xs text-gray-400">{lead.phone}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell text-sm text-gray-600">{lead.county}</td>
                      <td className="py-3 px-4 hidden lg:table-cell text-sm text-gray-500 max-w-[200px] truncate">{lead.product_name || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="relative inline-block">
                          <select value={lead.status} onChange={e => handleLeadStatusChange(lead.id, e.target.value)} className="appearance-none bg-transparent cursor-pointer pr-6 text-xs font-medium capitalize focus:outline-none" data-testid={`lead-status-select-${lead.id}`}>
                            {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                        </div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell text-xs text-gray-400">{formatDate(lead.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ORDERS TAB */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="data-table-container">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="orders-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Order ID</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Customer</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden md:table-cell">Items</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Total</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden md:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr><td colSpan={6} className="text-center text-gray-400 py-12 text-sm">No orders found</td></tr>
                  ) : filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`order-row-${order.id}`}>
                      <td className="py-3 px-4"><span className="text-sm font-mono text-gray-600">{order.id?.slice(0, 8)}...</span></td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">{order.customer_name}</div>
                        <div className="text-xs text-gray-400">{order.customer_email}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell"><span className="text-sm text-gray-600">{order.items?.length || 0} item(s)</span></td>
                      <td className="py-3 px-4"><span className="text-sm font-semibold text-gray-900">{formatCurrency(order.total)}</span></td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[order.payment_status || order.status] || 'bg-gray-100 text-gray-600'}`}>
                          {order.payment_status || order.status || 'pending'}
                        </span>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell text-xs text-gray-400">{formatDate(order.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* ABANDONED CARTS TAB */}
        {activeTab === 'abandoned' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="abandoned-carts-container">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="abandoned-carts-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Date</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Stage</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Customer</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden md:table-cell">Contact</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Items</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Value</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {abandonedCarts.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-gray-400 py-12 text-sm">No abandoned carts yet</td></tr>
                  ) : abandonedCarts.map((cart) => (
                    <tr key={cart.session_id} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${cart.recovered ? 'opacity-50' : ''}`} data-testid={`abandoned-row-${cart.session_id}`}>
                      <td className="py-3 px-4 text-xs text-gray-500">{formatDate(cart.updated_at || cart.created_at)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${cart.stage === 'checkout' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                          {cart.stage}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">{cart.customer_name || '-'}</div>
                        <div className="text-xs text-gray-400">{cart.customer_email || 'No email yet'}</div>
                      </td>
                      <td className="py-3 px-4 hidden md:table-cell">
                        <div className="text-sm text-gray-600">{cart.customer_phone || '-'}</div>
                        <div className="text-xs text-gray-400">{cart.county || '-'}</div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {(cart.items || []).map((item, i) => (
                          <div key={i} className="text-xs">{item.name || item.product_id}</div>
                        ))}
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-gray-900">{formatCurrency(cart.subtotal)}</td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleCartRecovered(cart.session_id, !cart.recovered)}
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${cart.recovered ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700 hover:bg-emerald-100 hover:text-emerald-700'}`}
                          data-testid={`cart-recover-${cart.session_id}`}
                        >
                          {cart.recovered ? 'Recovered' : 'Abandoned'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="products-table-container">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Product</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Model</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">MAP</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Sale</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden md:table-cell">Installed</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden lg:table-cell">Unit/mo</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 hidden lg:table-cell">Install/mo</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {products.sort((a, b) => parseFloat(a.tonnage) - parseFloat(b.tonnage)).map(p => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`product-row-${p.id}`}>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900">{p.tonnage}</div>
                        <div className="text-xs text-gray-400 max-w-[200px] truncate">{p.name}</div>
                      </td>
                      <td className="py-3 px-4 text-xs font-mono text-gray-500">{p.model_number}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatCurrency(p.map_price)}</td>
                      <td className="py-3 px-4 text-sm font-semibold text-emerald-600">{formatCurrency(p.sale_price)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 hidden md:table-cell">{formatCurrency(p.installed_price)}</td>
                      <td className="py-3 px-4 text-sm text-gray-500 hidden lg:table-cell">${p.unit_monthly?.toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm text-gray-500 hidden lg:table-cell">${p.installed_monthly?.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => setEditProduct(p)}
                          className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          data-testid={`product-edit-btn-${p.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SITE CONTENT TAB */}
        {activeTab === 'content' && <SiteContentEditor authHeaders={authHeaders} />}
      </main>

      {/* Product Edit Modal */}
      {editProduct && (
        <ProductEditModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSave={handleProductSave}
          saving={savingProduct}
        />
      )}
    </div>
  );
}
